import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../utils/firebase';
import Logo from '../components/Logo';

const PlayerHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scheduledGames, setScheduledGames] = useState([]);
  const [topTeams, setTopTeams] = useState([]);
  const [recentGames, setRecentGames] = useState([]);
  const [selectedCity, setSelectedCity] = useState('all');

  useEffect(() => {
    loadPublicData();
  }, []);

  const loadPublicData = async () => {
    try {
      setLoading(true);

      // Try to load recent games (may fail due to permissions)
      try {
        const gamesQuery = query(
          collection(db, 'gameResults'),
          orderBy('completedAt', 'desc'),
          limit(10)
        );
        const gamesSnapshot = await getDocs(gamesQuery);
        const gamesData = gamesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRecentGames(gamesData);
      } catch (error) {
        console.log('Could not load recent games (permissions):', error.message);
        setRecentGames([]);
      }

      // Try to load top teams (may fail due to permissions)
      try {
        const teamsQuery = query(
          collection(db, 'teams'),
          orderBy('gpa', 'desc'),
          limit(20)
        );
        const teamsSnapshot = await getDocs(teamsQuery);
        const teamsData = teamsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTopTeams(teamsData);
      } catch (error) {
        console.log('Could not load teams (permissions):', error.message);
        setTopTeams([]);
      }

      // Try to load scheduled games (may fail due to permissions)
      let scheduledGamesData = [];
      
      try {
        const scheduledGamesQuery = query(
          collection(db, 'scheduledGames'),
          where('status', '==', 'scheduled') // Only active games
        );
        const scheduledGamesSnapshot = await getDocs(scheduledGamesQuery);
        scheduledGamesData = scheduledGamesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Loaded games from scheduledGames collection:', scheduledGamesData.length);
      } catch (error) {
        console.log('Could not load from scheduledGames collection:', error.message);
        // Show sample data to encourage signup
        scheduledGamesData = [
          {
            id: 'sample-1',
            gameTitle: 'Tuesday Trivia Night',
            venueName: '57th Fighter Group Restaurant',
            venueAddress: '3829 Clairmont Rd, Atlanta, GA',
            hostName: 'Trivia Plug Pat',
            isRecurring: true,
            recurringDays: { tuesday: true, thursday: true },
            recurringTime: '19:00'
          },
          {
            id: 'sample-2', 
            gameTitle: 'Thursday Trivia Challenge',
            venueName: 'Local Bar & Grill',
            venueAddress: '123 Main St, Atlanta, GA',
            hostName: 'Quiz Master',
            isRecurring: true,
            recurringDays: { thursday: true },
            recurringTime: '20:00'
          }
        ];
      }
      
      setScheduledGames(scheduledGamesData);

    } catch (error) {
      console.error('Error loading public data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatRecurringSchedule = (game) => {
    if (!game.isRecurring || !game.recurringDays || !game.recurringTime) {
      return 'Schedule TBD';
    }

    const selectedDays = Object.entries(game.recurringDays)
      .filter(([_, checked]) => checked)
      .map(([day, _]) => day.charAt(0).toUpperCase() + day.slice(1));

    if (selectedDays.length === 0) {
      return 'Schedule TBD';
    }

    // Format time (HH:MM to 12-hour format)
    const [hours, minutes] = game.recurringTime.split(':').map(Number);
    const timeString = new Date(2000, 0, 1, hours, minutes).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return `Every ${selectedDays.join(', ')} at ${timeString}`;
  };

  const getCities = () => {
    const cities = scheduledGames.map(game => game.venue?.city).filter(Boolean);
    return ['all', ...new Set(cities)].sort();
  };

  const filteredGames = selectedCity === 'all' 
    ? scheduledGames 
    : scheduledGames.filter(game => game.venue?.city === selectedCity);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-white">Loading player hub...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Logo size={40} />
              <h1 className="text-2xl font-bold text-white">Player Hub</h1>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                ← Back to Home
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Join the League
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            Welcome to Trivia Plug
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Discover trivia games in your area, compete with teams worldwide, and join the ultimate trivia community.
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate('/signup')}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors"
            >
              🏆 Join the League
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gray-800 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-green-400 mb-2">
              {scheduledGames.length > 0 ? scheduledGames.length : 'Join to See'}
            </div>
            <div className="text-gray-300">Upcoming Games</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-blue-400 mb-2">
              {topTeams.length > 0 ? topTeams.length : 'Join to See'}
            </div>
            <div className="text-gray-300">Teams Competing</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 text-center">
            <div className="text-3xl font-bold text-purple-400 mb-2">
              {recentGames.length > 0 ? recentGames.length : 'Join to See'}
            </div>
            <div className="text-gray-300">Recent Games</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Scheduled Games Section */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">🎯 Find Games Near You</h3>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-1"
              >
                {getCities().map(city => (
                  <option key={city} value={city}>
                    {city === 'all' ? 'All Cities' : city}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {filteredGames.length > 0 ? (
                filteredGames.map(game => {
                  return (
                    <div key={game.id} className="bg-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-white flex items-center gap-2">
                            {game.gameTitle || 'Untitled Game'}
                            {game.isRecurring && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                🔄 Weekly
                              </span>
                            )}
                          </h4>
                          <p className="text-gray-300 text-sm">{game.venueName || 'Unknown Venue'}</p>
                          <p className="text-gray-400 text-xs">{game.venueAddress || ''}</p>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-blue-400 text-sm font-semibold">
                            {formatRecurringSchedule(game)}
                          </div>
                          <div className="text-gray-400 text-xs mt-1">
                            {game.hostId && game.hostName ? (
                              <button
                                onClick={() => navigate(`/profile/${game.hostId}`)}
                                className="text-blue-400 hover:text-blue-300 transition-colors underline"
                              >
                                {game.hostName}
                              </button>
                            ) : (
                              <span>{game.hostName || 'Host TBD'}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-lg mb-2">No Upcoming Games</div>
                  <p className="text-gray-500 text-sm mb-4">
                    Check back soon for upcoming trivia games in your area!
                  </p>
                  <div className="bg-gray-700 rounded-lg p-4 text-left">
                    <div className="text-center">
                      <p className="text-gray-300 text-sm mb-2">
                        Want to host a trivia game?
                      </p>
                      <button
                        onClick={() => navigate('/signup')}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                      >
                        Join as a Host
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Leaderboards Section */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-6">🏆 Global Leaderboard</h3>
            
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {topTeams.length > 0 ? (
                topTeams.map((team, index) => (
                  <div key={team.id} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          index === 0 ? 'bg-yellow-500 text-black' :
                          index === 1 ? 'bg-gray-400 text-black' :
                          index === 2 ? 'bg-orange-600 text-white' :
                          'bg-gray-600 text-white'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-semibold text-white">{team.name}</h4>
                          <p className="text-gray-300 text-sm">{team.homeVenue || 'Unknown Venue'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-green-400 font-bold">
                          GPA: {(team.gpa || 0).toFixed(2)}
                        </div>
                        <div className="text-gray-400 text-xs">
                          {team.leagueId || 'League TBD'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-lg mb-2">Join to See Leaderboards</div>
                  <p className="text-gray-500 text-sm mb-4">Sign up to compete and climb the rankings!</p>
                  <div className="space-y-3">
                    <div className="bg-gray-700 rounded-lg p-3 text-left">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center text-sm font-bold text-black">
                            1
                          </div>
                          <div>
                            <h4 className="font-semibold text-white">Sample Team</h4>
                            <p className="text-gray-300 text-sm">Sample Venue</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-green-400 font-bold">GPA: 3.85</div>
                          <div className="text-gray-400 text-xs">League TBD</div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-700 rounded-lg p-3 text-left">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-sm font-bold text-black">
                            2
                          </div>
                          <div>
                            <h4 className="font-semibold text-white">Another Team</h4>
                            <p className="text-gray-300 text-sm">Another Venue</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-green-400 font-bold">GPA: 3.72</div>
                          <div className="text-gray-400 text-xs">League TBD</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Games Section */}
        <div className="mt-8 bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-6">🎮 Recent Games</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentGames.length > 0 ? (
              recentGames.map(game => (
                <div key={game.id} className="bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">{game.gameData?.title || 'Untitled Game'}</h4>
                  <p className="text-gray-300 text-sm mb-2">
                    {game.venue?.name || 'Unknown Venue'}
                  </p>
                  <p className="text-gray-400 text-xs mb-3">
                    {game.completedAt?.toDate?.() ? 
                      game.completedAt.toDate().toLocaleDateString() : 
                      'Recently'
                    }
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-400 text-sm">
                      {game.teams?.length || 0} teams competed
                    </span>
                    <span className="text-green-400 text-sm font-semibold">
                      Winner: {game.teams?.[0]?.name || 'TBD'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-8">
                <div className="text-gray-400 text-lg mb-2">No recent games</div>
                <p className="text-gray-500 text-sm">Games will appear here as they're completed!</p>
              </div>
            )}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Join the Competition?
            </h3>
            <p className="text-green-100 mb-6 max-w-2xl mx-auto">
              Create your team, compete in trivia games, track your GPA, and climb the global leaderboards.
            </p>
            <button
              onClick={() => navigate('/signup')}
              className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
            >
              🏆 Join the League Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerHub; 